const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});

/**
 * Guards a missing date — `new Date(null)` is the epoch (not NaN), which rendered as "December 31, 1969".
 * @param date
 */
const formatDate = (date) => {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : dateFormatter.format(parsed);
};

const findDate = (flag, date, membershipInfo) =>
  membershipInfo.flags.filter((e) => e.display_name === flag)[0][date];

const flagsName = {
  has_box_subscription: 'has_box_subscription',
  canceled: 'canceled',
  canceled_box_subscription: 'canceled_box_subscription',
  canceled_trial_box_subscription: 'canceled_trial_box_subscription',
  canceled_refill_subscription: 'canceled_refill_subscription',
  has_refill_subscription: 'has_refill_subscription',
  expired_box_subscription: 'expired_box_subscription',
  expired_trial_box_subscription: 'expired_trial_box_subscription',
  trial: 'trial',
};

const labels = {
  expired: ['Membership Expired: ', 'Member Since: '],
  canceled: ['Membership Expires: ', 'Member Since: '],
  refill: ['Member Since: '],
};

/**
 * @param {object} membershipInfo - the membership endpoint payload (state + flags)
 * @param {string} [subscriptionState] - the status shown in the badge (core box for non-club
 *   members); takes precedence over `membership_state`, which an active refill keeps 'active' after
 *   the box is cancelled or lapses, so the dates always match the badge.
 * @returns {Array<{label: string, date?: string}>} the profile date rows
 */
export const getProfileDates = (membershipInfo, subscriptionState) => {
  try {
    let datesInfo = [];
    if (membershipInfo?.flags && membershipInfo?.flags.length === 0) return datesInfo;

    const state = subscriptionState?.toLowerCase() ?? membershipInfo?.membership_state;

    if (state === 'expired') {
      // A lapsed v2 trial carries `expired_trial_box_subscription` instead of the box flag.
      const findFlag = (name) => membershipInfo?.flags?.find((e) => e.display_name === name);
      const expiredFlag =
        findFlag(flagsName.expired_box_subscription) ??
        findFlag(flagsName.expired_trial_box_subscription);
      if (expiredFlag) {
        datesInfo = [
          {
            label: labels.expired[0],
            date: formatDate(expiredFlag.effective_from),
          },
          {
            label: labels.expired[1],
          },
        ];
      } else
        datesInfo = [
          {
            label: labels.expired[0],
            date: formatDate(findDate(flagsName.trial, 'effective_to', membershipInfo)),
          },
          {
            label: labels.expired[1],
          },
        ];
    }

    if (state === 'canceled') {
      const hasFlag = (name) => membershipInfo?.flags?.some((e) => e.display_name === name);
      // Expiry is the box's `canceled_box_subscription.effective_to`; the refill flag's is null.
      const isRefillOnly =
        hasFlag(flagsName.has_refill_subscription) && !hasFlag(flagsName.has_box_subscription);
      // A cancelled v2 trial carries `canceled_trial_box_subscription` instead of the box flag.
      const isCanceledTrial =
        hasFlag(flagsName.canceled_trial_box_subscription) &&
        !hasFlag(flagsName.canceled_box_subscription);
      const canceledFlag = isCanceledTrial
        ? flagsName.canceled_trial_box_subscription
        : isRefillOnly
          ? flagsName.canceled_refill_subscription
          : flagsName.canceled_box_subscription;
      datesInfo = [
        {
          label: labels.canceled[0],
          date: formatDate(findDate(canceledFlag, 'effective_to', membershipInfo)),
        },
        {
          label: labels.canceled[1],
        },
      ];
    }

    // A lapsed box drops `has_box_subscription`, so an active refill flags the member refill-only —
    // keep the box's expired/cancelled dates the badge already reflects.
    if (membershipInfo?.isRefillSubscription && state !== 'expired' && state !== 'canceled') {
      datesInfo = [
        {
          label: labels.refill[0],
        },
      ];
    }

    return datesInfo;
  } catch (_) {
    return [
      {
        label: labels.refill[0],
      },
    ];
  }
};
